import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  resumeAnonymizationCheck,
  runAnonymizationCheckCancel,
  runAnonymizationCheckRequest,
} from '../../../../store/actions/organizations';
import {
  selectIsRemovePIILoading,
  selectOrganizationById,
  selectRunAnonymizationCheck,
} from '../../../../store/selectors/organizations';
import { IRunCheckDateRange, RunAnonymizationCheckPhaseEnum } from '../../../../types/organizations';
import { getAnonymizationLastRunStatus } from '../../../../utils/organizations';
import { readPendingAnonymizationCheckFor } from '../../../../utils/runAnonymizationCheckStorage';

interface IParams {
  orgId?: string;
}

const useRunAnonymizationCheck = ({ orgId }: IParams) => {
  const dispatch = useDispatch();

  const selectSavedOrganization = useMemo(() => selectOrganizationById(orgId), [orgId]);
  const savedOrganization = useSelector(selectSavedOrganization);
  const isRemovePIILoading = useSelector(selectIsRemovePIILoading);
  const runState = useSelector(selectRunAnonymizationCheck);

  const [awaitingResumeOrgId, setAwaitingResumeOrgId] = useState<string | null>(() =>
    orgId && readPendingAnonymizationCheckFor(orgId) ? orgId : null,
  );
  const isAwaitingResume = !!orgId && awaitingResumeOrgId === orgId;

  const phase = useMemo(
    () => (orgId && runState.orgId === orgId ? runState.phase : RunAnonymizationCheckPhaseEnum.Idle),
    [orgId, runState],
  );

  const isAvailable = useMemo(() => !!orgId && !!savedOrganization, [orgId, savedOrganization]);

  useEffect(
    () => () => {
      dispatch(runAnonymizationCheckCancel());
    },
    [dispatch, orgId],
  );

  useEffect(() => {
    if (!orgId) {
      return;
    }

    const pending = readPendingAnonymizationCheckFor(orgId);

    if (pending) {
      dispatch(
        resumeAnonymizationCheck({
          orgId,
          baselineTimestamp: pending.baselineTimestamp,
          startedAt: pending.startedAt,
        }),
      );
    }

    setAwaitingResumeOrgId(null);
  }, [dispatch, orgId]);

  const runCheck = useCallback(
    ({ from, to }: IRunCheckDateRange) => {
      if (!orgId) {
        return;
      }

      dispatch(runAnonymizationCheckRequest({ orgId, from, to }));
    },
    [dispatch, orgId],
  );

  return {
    runCheck: isAvailable ? runCheck : undefined,
    phase,
    isRunning: isAwaitingResume || phase !== RunAnonymizationCheckPhaseEnum.Idle || isRemovePIILoading,
    lastRun: getAnonymizationLastRunStatus(savedOrganization),
  };
};

export default useRunAnonymizationCheck;
